import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import {
  GET_LIVE_CONTEXT_STATE_QUERY_KEY,
  type LiveContextStateDto,
} from '@/state/queries/player/use-get-live-context-state';
import { LiveContextShowMode } from '@/enums/live-context-show-mode.enum';
import type { LiveContextFocusMode } from '@/enums/live-context-focus-mode.enum';
import { entityRoomKeyFromEntityRef } from '@/state/player/perform-control-ws.store';
import {
  clearLivePlayStateOverlay,
  useLivePlayStateWsStore,
  type LivePlayStateEntry,
} from '@/state/player/live-play-state-ws.store';
import { shouldClearLivePlayStateOverlayAfterCueRefetch } from '@/helpers/merge-live-play-state.helper';
import { withFlowLiveContextShowMode } from '@/helpers/flow-live-context-show-mode.helper';

type OptimisticPicture = Partial<Pick<LivePlayStateEntry, 'showMode' | 'focusMode' | 'remoteSpeakersOnStage'>>;

function patchLivePicture(roomKey: string, patch: OptimisticPicture) {
  useLivePlayStateWsStore.setState((state) => {
    const entry = state.playStateByRoom[roomKey];
    if (!entry) {
      return state;
    }
    return {
      playStateByRoom: {
        ...state.playStateByRoom,
        [roomKey]: { ...entry, ...patch },
      },
    };
  });
}

/** On Air picture switches. Send only the field that changes; each one rebuilds the on-air layout. */
type UpdateLiveContextRequest = {
  entityType: 'Flow' | 'Event';
  entityId: string;
  showMode?: LiveContextShowMode;
  focusMode?: LiveContextFocusMode;
  remoteSpeakersOnStage?: boolean;
};

export const useUpdateLiveContextMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateLiveContextRequest) => {
      const token = await getToken();
      return updateLiveContext(body, token ?? '');
    },
    onMutate: (variables) => {
      const entityRef = `${variables.entityType}:${variables.entityId}`;
      const prev = queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef]);
      const { showMode, focusMode, remoteSpeakersOnStage } = variables;
      // On Air only — leave stagedSelection.showMode (Next) untouched. byPlayer updates after rebuild/WS.
      queryClient.setQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef], (current) => {
        const base: LiveContextStateDto = current ?? {};
        return {
          ...base,
          ...(focusMode != null ? { focusMode } : {}),
          ...(remoteSpeakersOnStage != null ? { remoteSpeakersOnStage } : {}),
          ...(showMode != null
            ? {
                playState: base.playState ? { ...base.playState, showMode } : { showMode, playedItemIds: [] },
              }
            : {}),
        };
      });
      const roomKey = entityRoomKeyFromEntityRef(entityRef);
      const prevWs = roomKey ? useLivePlayStateWsStore.getState().playStateByRoom[roomKey] : undefined;
      const picturePatch: OptimisticPicture = {
        ...(showMode != null ? { showMode } : {}),
        ...(focusMode != null ? { focusMode } : {}),
        ...(remoteSpeakersOnStage != null ? { remoteSpeakersOnStage } : {}),
      };
      if (roomKey && Object.keys(picturePatch).length > 0) {
        patchLivePicture(roomKey, picturePatch);
      }
      return {
        prev,
        entityRef,
        roomKey,
        prevWsShowMode: prevWs?.showMode,
        prevWsFocusMode: prevWs?.focusMode,
        prevWsRemoteSpeakersOnStage: prevWs?.remoteSpeakersOnStage,
      };
    },
    onError: (_err, variables, context) => {
      const ctx = context as
        | {
            prev?: LiveContextStateDto;
            entityRef?: string;
            roomKey?: string | null;
            prevWsShowMode?: string;
            prevWsFocusMode?: string;
            prevWsRemoteSpeakersOnStage?: boolean;
          }
        | undefined;
      if (ctx?.entityRef) {
        queryClient.setQueryData([GET_LIVE_CONTEXT_STATE_QUERY_KEY, ctx.entityRef], ctx.prev);
      }
      if (!ctx?.roomKey) {
        return;
      }
      const rollback: OptimisticPicture = {
        ...(variables.showMode != null ? { showMode: ctx.prevWsShowMode } : {}),
        ...(variables.focusMode != null ? { focusMode: ctx.prevWsFocusMode } : {}),
        ...(variables.remoteSpeakersOnStage != null ? { remoteSpeakersOnStage: ctx.prevWsRemoteSpeakersOnStage } : {}),
      };
      if (Object.keys(rollback).length > 0) {
        patchLivePicture(ctx.roomKey, rollback);
      }
    },
    onSuccess: async (_data, variables) => {
      // Patch instead of refetching: a full flow GET is megabytes and blocks the service for seconds.
      if (variables.entityType === 'Flow' && variables.showMode != null) {
        const showMode = variables.showMode;
        queryClient.setQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] }, (data: unknown) =>
          withFlowLiveContextShowMode(data, variables.entityId, showMode),
        );
      }
      // The PATCH returns after the rebuild is pushed and persisted, so this GET carries the new
      // `_preview:*` layout. Without it Current keeps the previous layout until it remounts.
      const entityRef = `${variables.entityType}:${variables.entityId}`;
      const queryKey = [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef];
      await queryClient.cancelQueries({ queryKey });
      await queryClient.refetchQueries({ queryKey });
      const roomKey = entityRoomKeyFromEntityRef(entityRef);
      if (!roomKey) {
        return;
      }
      const wsEntry = useLivePlayStateWsStore.getState().playStateByRoom[roomKey];
      if (shouldClearLivePlayStateOverlayAfterCueRefetch(queryClient.getQueryData(queryKey), wsEntry)) {
        clearLivePlayStateOverlay(roomKey);
      }
    },
  });
};

const updateLiveContext = async (body: UpdateLiveContextRequest, token: string): Promise<void> => {
  const url = `${import.meta.env.VITE_API_URL}/player/live-context`;
  await sendJSONRequest({ url, method: METHOD.PATCH, body, token });
};
